import { useState, useEffect, useCallback } from 'react';
import { projectTypesAPI } from '../utils/api';
import { useToast, apiError } from '../components/Toast';

/**
 * Reuses the project_comments backend (already supports entity_type='task').
 * Usage: <TaskComments projectId={task.project_id} taskId={task.id} currentUser={user} />
 */
export default function TaskComments({ projectId, taskId, currentUser }) {
  const toast = useToast();
  const [comments, setComments] = useState([]);
  const [error, setError] = useState('');
  const [loading,  setLoading]  = useState(true);
  const [body,     setBody]     = useState('');
  const [posting,  setPosting]  = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await projectTypesAPI.getComments(projectId, { entity_type:'task', entity_id:taskId });
      setComments(r.data);
    } catch(e) { toast.error(apiError(e, 'Failed to post comment')); } finally { setLoading(false); }
  }, [projectId, taskId]);

  useEffect(() => { load(); }, [load]);

  const handlePost = async () => {
    if (!body.trim()) return;
    setPosting(true);
    try {
      await projectTypesAPI.createComment(projectId, { body, entity_type:'task', entity_id:taskId });
      setBody(''); load();
    } catch(e) { toast.error(apiError(e, 'Failed to post comment')); } finally { setPosting(false); }
  };

  const handleDelete = async (id) => {
    await projectTypesAPI.deleteComment(id); load();
  };

  return (
    <div className="card">
      {error && (
        <div className="mx-auto max-w-screen-xl px-4 pt-2">
          <div className="p-3 rounded-xl text-sm mb-2" style={{ background:'rgba(239,68,68,.08)', color:'#ef4444', border:'1px solid rgba(239,68,68,.2)' }}>
            ⚠ {error} <button className="ml-2 underline text-xs" onClick={()=>setError('')}>Dismiss</button>
          </div>
        </div>
      )}
      <h3 className="font-semibold text-sm mb-3" style={{ color:'var(--text)' }}>
        Activity Feed {comments.length>0 && <span className="text-xs" style={{ color:'var(--text-secondary)' }}>({comments.length})</span>}
      </h3>

      <div className="flex gap-2 mb-4">
        <textarea className="input flex-1 text-sm" rows={2} placeholder="Add a comment..."
          value={body} onChange={e=>setBody(e.target.value)}
          onKeyDown={e=>{ if(e.key==='Enter' && !e.shiftKey){ e.preventDefault(); handlePost(); } }}/>
        <button onClick={handlePost} disabled={posting||!body.trim()} className="btn-primary text-xs self-end px-3 py-2">
          {posting ? '...' : 'Post'}
        </button>
      </div>

      {loading ? (
        <p className="text-xs text-center py-3" style={{ color:'var(--text-secondary)' }}>Loading...</p>
      ) : comments.length === 0 ? (
        <p className="text-xs text-center py-3" style={{ color:'var(--text-secondary)' }}>No comments yet - start the conversation</p>
      ) : (
        <div className="space-y-3">
          {comments.map(c => (
            <div key={c.id} className="flex gap-2.5">
              <div className="w-7 h-7 rounded-lg flex items-center justify-center font-bold text-white text-xs flex-shrink-0"
                style={{ background:c.author_color||'#2E539F' }}>{c.author_name?.[0]}</div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold" style={{ color:'var(--text)' }}>{c.author_name}</span>
                  <span className="text-xs" style={{ color:'var(--text-secondary)' }}>{new Date(c.created_at).toLocaleString()}</span>
                  {currentUser?.id===c.author_id && (
                    <button onClick={()=>handleDelete(c.id)} className="text-xs text-red-400 hover:text-red-600 ml-auto">Delete</button>
                  )}
                </div>
                <p className="text-sm mt-0.5" style={{ color:'var(--text)' }}>{c.body}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
